import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";

const GCS_AUDIO_URL =
  "https://storage.googleapis.com/mindsherpa-app-bucket/audios/Wavelengths-Alpha.mp3";

const MindfulnessMusicContext = createContext(null);

export const MindfulnessMusicProvider = ({ children }) => {
  const location = useLocation();
  const audioRef = useRef(null);

  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.25);
  const [isMuted, setIsMuted] = useState(false);

  // Track if user explicitly paused the background music
  const userManuallyPausedRef = useRef(false);
  // Track if background music was paused temporarily because an exercise audio/video is playing
  const pausedForGuidanceRef = useRef(false);

  const pathname = (location.pathname || "").toLowerCase();
  const isMindfulnessRoute =
    pathname.startsWith("/mindfulness") || pathname.startsWith("/exercises");

  const playAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const promise = audio.play();
    if (promise !== undefined) {
      promise
        .then(() => {
          setIsMusicPlaying(true);
        })
        .catch((err) => {
          // Autoplay restricted until user interaction
          console.warn("Autoplay / audio play blocked or pending gesture:", err?.message || err);
          setIsMusicPlaying(false);
        });
    }
  }, []);

  const pauseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      setIsMusicPlaying(false);
    }
  }, []);

  // Sync background audio with navigation
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMindfulnessRoute) {
      // If we returned to the mindfulness catalogue from an exercise, clear guidance pause
      if (!pathname.startsWith("/exercises")) {
        pausedForGuidanceRef.current = false;
      }

      // If user hasn't manually paused and not currently listening to an exercise, ensure music is playing
      if (!userManuallyPausedRef.current && !pausedForGuidanceRef.current) {
        if (audio.paused) {
          playAudio();
        }
      }
    } else {
      // Left mindfulness section entirely -> pause background music
      if (!audio.paused) {
        pauseAudio();
      }
    }
  }, [isMindfulnessRoute, pathname, playAudio, pauseAudio]);

  // First user interaction gesture listener (bypasses browser autoplay block seamlessly)
  useEffect(() => {
    const handleFirstGesture = () => {
      const audio = audioRef.current;
      const currentPath = (window.location.pathname || "").toLowerCase();
      const onMindfulness =
        currentPath.startsWith("/mindfulness") || currentPath.startsWith("/exercises");

      if (
        audio &&
        audio.paused &&
        onMindfulness &&
        !userManuallyPausedRef.current &&
        !pausedForGuidanceRef.current
      ) {
        playAudio();
      }
      window.removeEventListener("click", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };

    window.addEventListener("click", handleFirstGesture, { once: true });
    window.addEventListener("keydown", handleFirstGesture, { once: true });

    return () => {
      window.removeEventListener("click", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
  }, [playAudio]);

  // Sync state with audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.25;

    const onPlay = () => setIsMusicPlaying(true);
    const onPause = () => setIsMusicPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, []);

  const toggleMusic = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      userManuallyPausedRef.current = false;
      pausedForGuidanceRef.current = false;
      playAudio();
    } else {
      userManuallyPausedRef.current = true;
      pauseAudio();
    }
  }, [playAudio, pauseAudio]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.muted || isMuted) {
      audio.muted = false;
      setIsMuted(false);
    } else {
      audio.muted = true;
      setIsMuted(true);
    }
  }, [isMuted]);

  const setVolume = useCallback(
    (newVol) => {
      const audio = audioRef.current;
      const clamped = Math.max(0, Math.min(1, newVol));
      setVolumeState(clamped);
      if (audio) {
        audio.volume = clamped;
        if (clamped > 0 && isMuted) {
          audio.muted = false;
          setIsMuted(false);
        }
      }
    },
    [isMuted]
  );

  // Called when exercise audio/video begins playing
  const pauseForGuidance = useCallback(() => {
    pausedForGuidanceRef.current = true;
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      pauseAudio();
    }
  }, [pauseAudio]);

  // Called when exercise audio/video is paused or finished
  const resumeAfterGuidance = useCallback(() => {
    pausedForGuidanceRef.current = false;
    const currentPath = (window.location.pathname || "").toLowerCase();
    const onMindfulness =
      currentPath.startsWith("/mindfulness") || currentPath.startsWith("/exercises");

    if (onMindfulness && !userManuallyPausedRef.current) {
      playAudio();
    }
  }, [playAudio]);

  const value = {
    isMusicPlaying,
    toggleMusic,
    isMuted,
    toggleMute,
    volume,
    setVolume,
    pauseForGuidance,
    resumeAfterGuidance,
    isMindfulnessRoute,
  };

  return (
    <MindfulnessMusicContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        src={GCS_AUDIO_URL}
        loop
        preload="metadata"
        onError={(err) => {
          console.warn("GCP background music error:", err);
          setIsMusicPlaying(false);
        }}
        onEnded={() => {
          if (
            audioRef.current &&
            !userManuallyPausedRef.current &&
            !pausedForGuidanceRef.current
          ) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          }
        }}
      />
    </MindfulnessMusicContext.Provider>
  );
};

export const useMindfulnessMusic = () => {
  const context = useContext(MindfulnessMusicContext);
  if (!context) {
    throw new Error(
      "useMindfulnessMusic must be used within a MindfulnessMusicProvider"
    );
  }
  return context;
};
